import {
	AfterViewInit,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	inject,
	Input,
	NgZone,
	OnDestroy,
	Output,
	signal,
} from '@angular/core';

import * as am5 from '@amcharts/amcharts5';
import { Sprite } from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';

import { TimeSeriesSettings } from '@app/pages/cmms/components/assets/asset-time-series/misc/interfaces';
import { ChartModalEditFormComponent } from '@app/pages/cmms/components/assets/asset-time-series/chart-with-settings/chart-graph/chart-modal-edit-form/chart-modal-edit-form.component';
import { AuAmchartHelper } from '@app/pages/cmms/components/assets/asset-time-series/chart-with-settings/chart-graph/au-amchart-helper';

/** Useful examples
 *
 * 		example of date formats
 * 	  xAxis.get('dateFormats')['day'] = 'MM/dd';
 * 	  xAxis.get('periodChangeDateFormats')['day'] = 'MMM';
 * */

/**  Examples of bullet tooltip template
 * const bulletTooltipTemplate: string = `
 *					<div style="padding: 0px; background-color: #ffffff; box-shadow: 0 0 10px rgba(0,0,0,.1);">
 *					<h4 style="color: #000000;">{categoryX}</h4>
 *					<p>Value: <strong style="color: #0000ff;">{valueY}</strong></p>
 *<!--					<p>Autor: <span style="color: #0000ff;">{createdBy.displayName}</span></p>-->
 *					<p>Click to point for edit value</p>
 *					</div>
 *				`;
 */

export enum ChartHexadecimalFormatColors {
	RED = 0xff0000,
	BLUE = 0x0000ff,
	GREEN = 0x00ff00,
	YELLOW = 0xffff00,
	BLACK = 0x000000,
	WHITE = 0xffffff,
}

@Component({
	selector: 'chart-graph',
	templateUrl: './chart-graph.component.html',
	styleUrls: ['./chart-graph.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [ChartModalEditFormComponent],
})
export class ChartGraphComponent implements AfterViewInit, OnDestroy {
	@Output() editPoint = new EventEmitter<{ value: number; date: Date }>();
	@Output() deletePoint = new EventEmitter<{ value: number; date: Date }>();

	@Input() uniqueIdForRoot: string;
	private readonly cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
	private zone: NgZone = inject(NgZone);

	@Input() set timeSeriesData(data: { date: Date; value: number }[]) {
		this.cloneNormalizeAndSetDataSequence(data);
	}

	@Input() set chartSettings(value: TimeSeriesSettings) {
		this.settings = value;
		setTimeout(() => {
			this.applyChartSettings();
		});
	}

	// TODO use signals
	contextForPointEdit: { [key: string]: any };
	settings: TimeSeriesSettings = {
		upperBound: null,
		lowerBound: null,
		benchmark: null,
		unit: null,
	};

	data = null;
	showModal = signal(false);
	root: am5.Root | undefined;

	private chart: am5xy.XYChart | undefined;
	private xAxis: am5xy.CategoryAxis<any> | undefined;
	private yAxis: am5xy.ValueAxis<any> | undefined;
	private series: am5xy.LineSeries | undefined;
	private lineLinksArray = [];
	private yAxisTitle: Sprite | undefined;

	ngAfterViewInit() {
		this.zone.runOutsideAngular(() => {
			this.createChart();
		});
	}

	private createChart() {
		const root = am5.Root.new(this.uniqueIdForRoot);
		this.root = root;

		root.setThemes([am5themes_Animated.new(root)]);

		// for chart creation we need to create chart, x and y axis, and series
		const chart = AuAmchartHelper.createChart(root);
		this.xAxis = AuAmchartHelper.createXAxis(root, chart, this.data);

		// rotate dates to 90 degrees
		const xRenderer = this.xAxis.get('renderer');
		xRenderer.labels.template.setAll({
			rotation: -90,
			centerY: am5.p50,
		});

		this.yAxis = AuAmchartHelper.createYAxis(root, chart);
		this.series = AuAmchartHelper.createSeries(root, chart, this.xAxis, this.yAxis);
		// then we need to set data to the series
		this.series.data.setAll(this.data);
		// then create bullets
		this.series.bullets.push(() => {
			const bullet = AuAmchartHelper.createBullet(root, this.series);
			// Add a click event listener to the bullet's sprite
			bullet.get('sprite').events.on('click', ev => {
				// Access the data item associated with the clicked bullet
				const dataItem = ev.target.dataItem;

				setTimeout(() => {
					this.showModal.set(true);
					this.contextForPointEdit = dataItem.dataContext;
					this.cdr.detectChanges();
				});

				// Perform actions here, e.g., display more information or update UI
			});

			return bullet;
		});

		this.series.strokes.template.set('strokeWidth', 2);

		// Add crosshair cursor
		AuAmchartHelper.setCursor(chart, root, this.xAxis);

		// add tooltips for axes
		AuAmchartHelper.setTooltipForAxis(root, this.xAxis);
		AuAmchartHelper.setTooltipForAxis(root, this.yAxis);

		// example of tooltip format
		// AuAmchartHelper.setTooltipForSeriesBullet(this.series, bulletTooltipTemplate);
		this.chart = chart;
	}

	private cloneNormalizeAndSetDataSequence(data: { date: Date; value: number }[]) {
		this.data = structuredClone(data);
		this.data.forEach(d => {
			d.date = new Date(d.datetime).getTime();
		});

		this.chart?.series.each(series => {
			series.data.setAll(this.data);
		});
	}

	private applyChartSettings() {
		if (this.lineLinksArray.length) {
			this.lineLinksArray.forEach(lineLink => {
				lineLink.dispose();
			});

			this.lineLinksArray = [];
		}

		if (this.settings?.lowerBound) {
			const lineLink = AuAmchartHelper.createRangeLine(this.yAxis, {
				value: this.settings.lowerBound,
				color: ChartHexadecimalFormatColors.RED,
				label: 'Low.',
				strokeWidth: 4,
				strokeDasharray: [3],
			});

			this.lineLinksArray.push(lineLink);
		}

		if (this.settings?.upperBound) {
			const lineLink = AuAmchartHelper.createRangeLine(this.yAxis, {
				value: this.settings.upperBound,
				color: ChartHexadecimalFormatColors.RED,
				label: 'Upp.',
				strokeWidth: 4,
				strokeDasharray: [3],
			});

			this.lineLinksArray.push(lineLink);
		}

		if (this.settings?.benchmark) {
			const lineLink = AuAmchartHelper.createRangeLine(this.yAxis, {
				value: this.settings.benchmark,
				color: ChartHexadecimalFormatColors.GREEN,
				label: 'Ben.',
				strokeWidth: 4,
				strokeDasharray: [0],
			});

			this.lineLinksArray.push(lineLink);
		}

		if (this.settings?.unit) {
			if (!this.yAxisTitle) {
				this.yAxisTitle = am5.Label.new(this.root, {
					rotation: -90,
					text: `Units (${this.settings.unit})`,
					y: am5.p50,
					centerX: am5.p50,
				});

				this.yAxis.children.unshift(this.yAxisTitle);
			} else {
				this.yAxisTitle.set('text' as any, `Units (${this.settings.unit})`);
			}
		}
	}

	closeModal() {
		this.showModal.set(false);
	}

	ngOnDestroy() {
		if (this.chart) {
			this.chart.dispose();
		}
	}
}
